import { Check, LoaderCircle, Plug } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useGcpProjectPicker } from './use-gcp-project-picker';

interface GcpProjectPickerProps {
  onProjectSelect: (projectId: string) => void;
}

export function GcpProjectPicker({ onProjectSelect }: GcpProjectPickerProps) {
  const f = useGcpProjectPicker({ onProjectSelect });

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-2xl">
        {f.loading && (
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground flex items-center">
                <LoaderCircle className="h-5 w-5 mr-2 animate-spin" />
                Loading projects...
              </div>
            </CardContent>
          </Card>
        )}

        {f.error && (
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-destructive mb-4">{f.error}</div>
              <Button onClick={f.fetchProjects} variant="outline" size="sm">
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {!f.loading && !f.error && f.projects.length === 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground">No projects found.</div>
            </CardContent>
          </Card>
        )}

        {!f.loading && !f.error && f.projects.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Plug className="h-5 w-5 mr-2" />
                Select GCP Project
              </h3>
              <div className="space-y-2 mb-4">
                {f.projects.map((project) => (
                  <div
                    key={project.projectId}
                    className={`flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${
                      f.selectedProjectId === project.projectId
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => f.handleItemClick(project.projectId)}
                  >
                    <div>
                      <div className="font-medium">{project.name}</div>
                      <div className="text-sm text-muted-foreground">{project.projectId}</div>
                    </div>
                    {f.selectedProjectId === project.projectId && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </div>
                ))}
              </div>
              <Button onClick={f.handleSelect} disabled={!f.selectedProjectId} className="w-full">
                Continue with Selected Project
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
